import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../api';
import { useAuth } from '../context/AuthContext';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import SearchInput from '../components/SearchInput';
import PortalIcon from '../components/PortalIcon';
import { TIERS, tenantLicense } from '../utils/license';
import './styles.css';
import { Svg, initialsOf, useSort, sortBy, SortHead } from '../components/PortalUI';
import '../components/PortalUI.css';
import LoadingState from '../components/LoadingState';

export default function Tenants() {
  const navigate = useNavigate();
  const { role, permissions, isOwner } = useAuth();
  // A NordComply team member deletes a customer only with the right to; an organisation's own admin as before.
  const canDelete = role !== 'provider' || isOwner || permissions.includes('delete');
  const { setAddTenantOpen } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = searchParams.get('org');
  const orgName = searchParams.get('orgName');

  // A provider hitting /tenants directly (bookmark, browser history, typing
  // the URL) instead of going through Organisationer → pick an org no
  // longer gets an unscoped cross-org list from the API (see
  // handleGetTenants) — it now comes back empty. Redirect to Organisationer
  // instead of rendering that empty state, so this reads as "wrong place",
  // not "broken".
  useEffect(() => {
    if (role === 'provider' && !orgId) {
      navigate('/organizations', { replace: true });
    }
  }, [role, orgId, navigate]);
  const [tenants, setTenants] = useState([]);
  const [overviewById, setOverviewById] = useState({});
  const [loading, setLoading] = useState(true);
  const showLoading = loading;
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // The recycle bin: customers removed in the last 7 days. NordComply staff with the right to delete see it and can restore or remove for good.
  const canTrash = role === 'provider' && (isOwner || permissions.includes('delete'));
  const [trash, setTrash] = useState([]);
  const [trashBusy, setTrashBusy] = useState('');
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgeName, setPurgeName] = useState('');
  const [purgeError, setPurgeError] = useState('');
  async function fetchTrash() {
    if (!canTrash) return;
    try {
      const res = await apiFetch(`/api/tenants-trash${orgId ? `?orgId=${orgId}` : ''}`);
      if (res.ok) setTrash((await res.json()).tenants || []);
    } catch { /* the bin is optional */ }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTrash(); }, [orgId, canTrash]);
  async function restoreTenant(t) {
    setTrashBusy(t.tenantId);
    try {
      const res = await apiFetch('/api/tenant-restore', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tenantId: t.tenantId }) });
      if (!res.ok) throw new Error('Kunne ikke gendanne');
      await Promise.all([fetchTrash(), fetchTenants({ silent: true })]);
    } catch (err) {
      console.error(err);
    } finally {
      setTrashBusy('');
    }
  }
  async function purgeTenant() {
    setPurgeError('');
    setTrashBusy(purgeTarget.tenantId);
    try {
      const res = await apiFetch('/api/tenant-purge', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tenantId: purgeTarget.tenantId, confirmName: purgeName }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Kunne ikke slette');
      setPurgeTarget(null);
      setPurgeName('');
      await fetchTrash();
    } catch (err) {
      setPurgeError(err.message);
    } finally {
      setTrashBusy('');
    }
  }
  const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
  const [search, setSearch] = useState('');
  const [licenseFilter, setLicenseFilter] = useState('all');
  const [toast, setToast] = useState(searchParams.get('tenant_added') === 'true');
  const exchangeConnected = searchParams.get('exchange_connected');
  const [exchangeToast, setExchangeToast] = useState(
    exchangeConnected != null
      ? { ok: exchangeConnected === 'true', reason: searchParams.get('reason') || '' }
      : null
  );

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(false), 5000);
      searchParams.delete('tenant_added');
      setSearchParams(searchParams, { replace: true });
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (exchangeToast) {
      const t = setTimeout(() => setExchangeToast(null), 6000);
      searchParams.delete('exchange_connected');
      searchParams.delete('reason');
      // tenantId stays until the tenant list has loaded (below), so the
      // panel can still be opened for the right tenant after this redirect.
      setSearchParams(searchParams, { replace: true });
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No longer auto-opens EditTenantPanel for the tenant named in the
  // redirect's ?tenantId= — per direct feedback, the tab that carries an
  // OAuth redirect (Graph or Exchange) is just a side effect of
  // AddTenantModal's own "Godkend" buttons; the modal's own "Færdig" step
  // is the real confirmation UX. This tab should just land quietly on the
  // plain tenant list, not surprise the admin with an already-open panel.
  useEffect(() => {
    if (searchParams.get('tenantId')) {
      searchParams.delete('tenantId');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTenants();
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // `silent` refreshes the list in the background (after saving a licence,
  // say) instead of swapping the whole list for a loading state.
  async function fetchTenants({ silent } = {}) {
    try {
      if (!silent) setLoading(true);
      const res = await apiFetch(`/api/tenants${orgId ? `?orgId=${orgId}` : ''}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setTenants(data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchOverview() {
    try {
      const res = await apiFetch(`/api/tenants-overview${orgId ? `?orgId=${orgId}` : ''}`);
      const data = await res.json();
      if (Array.isArray(data.tenants)) {
        setOverviewById(Object.fromEntries(data.tenants.map((t) => [t.id, t])));
      }
    } catch (err) {
      console.error('Error:', err);
    }
  }

  async function handleDeleteTenant() {
    const res = await apiFetch(`/api/tenant?tenantId=${deleteConfirm.tenantId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Kunne ikke slette tenant');
    setDeleteConfirm(null);
    await fetchTenants();
    await fetchTrash();
  }

  // Connection is the only state worth surfacing on this list: whether a
  // tenant has open findings says nothing here (nearly every tenant does),
  // but a broken connection or a needed re-consent is something an admin
  // can act on. Those float to the top, then a still-checking tenant.
  const connectionOf = (id) => {
    const o = overviewById[id];
    if (!o) return 'checking';
    return o.connectionOk && !o.needsReauth ? 'ok' : 'bad';
  };

  const [sort, toggleSort] = useSort('conn');
  const sorted = useMemo(() => {
    const rank = { bad: 0, checking: 1, ok: 2 };
    const q = search.trim().toLowerCase();
    const shown = tenants
      .filter((tn) => !q || tn.name.toLowerCase().includes(q))
      .filter((tn) => licenseFilter === 'all' || tn.license?.tier === licenseFilter);
    // By connection, with the broken ones first, then by name, unless another heading is chosen.
    const byName = [...shown].sort((x, y) => x.name.localeCompare(y.name, 'da'));
    return sortBy(byName, sort, {
      name: (tn) => tn.name,
      conn: (tn) => rank[connectionOf(tn.id)],
      license: (tn) => tenantLicense(tn) || '',
      added: (tn) => (tn.addedAt ? new Date(tn.addedAt).getTime() : null),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenants, search, overviewById, licenseFilter, sort]);

  return (
    <>
      {toast && (
        <div className="toast">
          <div className="toast__icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
          </div>
          <div className="toast__text">
            <b>Tenant tilføjet</b>Live-data hentes nu — det tager typisk under et minut.
          </div>
          <button className="toast__close" onClick={() => setToast(false)}>×</button>
        </div>
      )}

      {exchangeToast && (
        // Both toasts now fire together for a first-time tenant add (the
        // Exchange leg is chained right after) — stacked above the tenant
        // one instead of sharing its fixed bottom-right spot.
        <div className="toast" style={toast ? { bottom: 'calc(var(--space-6) + 64px)' } : undefined}>
          <div className={`toast__icon${exchangeToast.ok ? '' : ' toast__icon--error'}`}>
            {exchangeToast.ok ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            )}
          </div>
          <div className="toast__text">
            <b>{exchangeToast.ok ? 'Exchange Online forbundet' : 'Exchange Online-forbindelse fejlede'}</b>
            {exchangeToast.ok ? 'Postkasse-typer (delt/rum/udstyr) genkendes nu korrekt.' : (exchangeToast.reason || 'Prøv igen fra tenantens redigeringspanel.')}
          </div>
          <button className="toast__close" onClick={() => setExchangeToast(null)}>×</button>
        </div>
      )}

      <div className="pu-page">
        {orgName && (
          <button type="button" className="pu-back" onClick={() => navigate('/organizations')}>
            <Svg name="back" size={13} />
            Organisationer
          </button>
        )}
        <div className="pu-head">
          <div>
            <h1>{orgName ? `${orgName} · Kunde tenants` : 'Kunde tenants'}</h1>
            <p>{orgName ? 'Organisationens kunder. Klik på en kunde for at se oplysninger og brugere.' : 'Jeres kunder. Klik på en kunde for at se oplysninger og brugere.'}</p>
          </div>
          <button type="button" className="pu-btn pu-btn--p" onClick={() => setAddTenantOpen(true)}><Svg name="monitor" />Tilføj kunde</button>
        </div>

        {showLoading ? (
          <LoadingState />
        ) : tenants.length === 0 ? (
          <div className="pu-empty">
            <p>Du har ingen kunder endnu</p>
            <button onClick={() => setAddTenantOpen(true)} className="pu-btn pu-btn--p">Tilføj din første kunde</button>
          </div>
        ) : (
          <>
            <div className="pu-bar">
              <SearchInput value={search} onChange={setSearch} placeholder="Søg efter kunde..." />
              <div className="pu-seg" role="group" aria-label="Licens">
                <button type="button" className={licenseFilter === 'all' ? 'is-on' : ''} onClick={() => setLicenseFilter('all')}>Alle licenser</button>
                {TIERS.map((tier) => (
                  <button key={tier.key} type="button" className={licenseFilter === tier.key ? 'is-on' : ''} onClick={() => setLicenseFilter((prev) => (prev === tier.key ? 'all' : tier.key))}>{tier.label}</button>
                ))}
              </div>
            </div>
            {sorted.length === 0 ? (
              <div className="pu-empty"><p>Ingen kunder matcher din søgning</p></div>
            ) : (
              <div className="pu-table">
                <div className="pu-th pu-tr pu-tr--t"><SortHead label="Kunde" k="name" sort={sort} onSort={toggleSort} /><SortHead label="Forbindelse" k="conn" sort={sort} onSort={toggleSort} /><SortHead label="Licens" k="license" sort={sort} onSort={toggleSort} /><SortHead label="Tilføjet" k="added" sort={sort} onSort={toggleSort} /><span /><span /></div>
                {sorted.map((tenant) => {
                  const conn = connectionOf(tenant.id);
                  const overview = overviewById[tenant.id];
                  const customerUrl = (tab) => { const q = [tab ? 'tab=' + tab : '', orgId ? 'org=' + orgId + '&orgName=' + encodeURIComponent(orgName || '') : ''].filter(Boolean).join('&'); return '/customer/' + tenant.id + (q ? '?' + q : ''); };
                  // Ctrl/Cmd-click or middle-click opens in a new tab; a plain click stays in this one.
                  const customerPage = (tab, e) => { if (e && (e.ctrlKey || e.metaKey)) { window.open(customerUrl(tab), '_blank', 'noopener'); return; } navigate(customerUrl(tab)); };
                  return (
                    <div
                      key={tenant.id}
                      className="pu-tr pu-tr--t pu-tr--click"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => customerPage(undefined, e)}
                      onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); window.open(customerUrl(), '_blank', 'noopener'); } }}
                      onKeyDown={(e) => { if (e.key === 'Enter') customerPage(); }}
                    >
                      <span className="pu-who">
                        <span className="pu-av">{initialsOf(tenant.name)}</span>
                        <span><b>{tenant.name}</b><small>{tenant.domain || tenant.primaryDomain || ''}</small></span>
                      </span>
                      <span title={overview?.missingPermissions?.length ? `Mangler godkendelse af: ${overview.missingPermissions.join(', ')}` : undefined}>
                        <span className={`pu-st${conn === 'bad' ? ' pu-st--warn' : conn === 'ok' ? ' pu-st--ok' : ''}`}><i />{conn === 'checking' ? 'Tjekker…' : conn === 'bad' ? (overview?.needsReauth ? 'Kræver godkendelse' : 'Fejl ved forbindelse') : 'Forbundet'}</span>
                      </span>
                      <span><span className={`pu-chip ${tenant.license?.tier === 'trial' || tenant.license?.expired ? 'pu-chip--warn' : 'pu-chip--neu'}`}>{tenantLicense(tenant) || 'Ingen licens'}</span></span>
                      <span className="pu-c pu-c--m">{tenant.addedAt ? new Date(tenant.addedAt).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                      <span className="pu-acts">
                        {overview?.needsReauth && (
                          <button type="button" className="pu-ib" title="Genautentificér kunden" aria-label="Genautentificér kunden" onClick={(e) => { e.stopPropagation(); customerPage('con'); }}><PortalIcon name="refresh" /></button>
                        )}
                        {canDelete && <button type="button" className="pu-ib pu-ib--del" title="Slet kunde" aria-label="Slet kunde" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(tenant); }}><PortalIcon name="trash" /></button>}
                      </span>
                      <span className="pu-acts">
                        <a href={`/tenant/${tenant.id}`} className="pu-btn pu-btn--sm" title="Åbn kundens portal" onClick={(e) => { e.stopPropagation(); if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); navigate(`/tenant/${tenant.id}`); }}>Portal</a>
                        <a href={`/tenant/${tenant.id}`} target="_blank" rel="noopener noreferrer" className="pu-ib" title="Åbn kundens portal i ny fane" aria-label="Åbn kundens portal i ny fane" onClick={(e) => e.stopPropagation()}><Svg name="open" size={14} /></a>
                        <span className="pu-go"><Svg name="chevron" size={15} /></span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="pu-foot">Dem med problemer står øverst. Knappen Portal åbner kundens egen portal.</p>
          </>
        )}

        {canTrash && trash.length > 0 && (
          <div className="pu-trash">
            <h3>Fjernede kunder</h3>
            <p>Kunderne her er skjult for alle og slettes endeligt efter 7 dage. Indtil da kan de gendannes.</p>
            <div className="pu-table">
              {trash.map((t) => (
                <div key={t.tenantId} className="pu-tr pu-tr--trash">
                  <span className="pu-who">
                    <span className="pu-av">{initialsOf(t.name)}</span>
                    <span><b>{t.name}</b><small>Fjernet {new Date(t.deletedAt).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}{t.deletedBy ? ` af ${t.deletedBy}` : ''}</small></span>
                  </span>
                  <span className="pu-c">Slettes endeligt {new Date(t.purgeAt).toLocaleDateString('da-DK', { day: 'numeric', month: 'long' })} ({daysLeft(t.purgeAt) === 0 ? 'i dag' : `om ${daysLeft(t.purgeAt)} ${daysLeft(t.purgeAt) === 1 ? 'dag' : 'dage'}`})</span>
                  <span className="pu-acts">
                    <button type="button" className="pu-btn pu-btn--sm" disabled={trashBusy === t.tenantId} onClick={() => restoreTenant(t)}>Gendan</button>
                    <button type="button" className="pu-btn pu-btn--sm pu-btn--danger" disabled={trashBusy === t.tenantId} onClick={() => { setPurgeTarget(t); setPurgeName(''); setPurgeError(''); }}>Slet permanent</button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {purgeTarget && (
        <div className="pu-overlay" role="dialog" aria-modal="true" onClick={() => setPurgeTarget(null)}>
          <div className="pu-modal pu-modal--s pu-trashm" onClick={(e) => e.stopPropagation()}>
            <h3>Slet "{purgeTarget.name}" permanent?</h3>
            <p>Alt slettes nu og kan ikke gendannes: aflæsninger, historik, beslutninger, noter, IT-opgaver, rapportplan og brugere, der kun havde adgang til denne kunde. Du får en bekræftelse på mail bagefter.</p>
            <label className="pu-field">Skriv kundens navn for at bekræfte
              <input type="text" value={purgeName} onChange={(e) => setPurgeName(e.target.value)} placeholder={purgeTarget.name} autoFocus />
            </label>
            {purgeError && <p className="state-error">{purgeError}</p>}
            <div className="pu-btns">
              <button type="button" className="pu-btn" onClick={() => setPurgeTarget(null)}>Annullér</button>
              <button type="button" className="pu-btn pu-btn--danger" disabled={purgeName.trim() !== purgeTarget.name.trim() || trashBusy === purgeTarget.tenantId} onClick={purgeTenant}>{trashBusy === purgeTarget.tenantId ? 'Sletter…' : 'Slet permanent'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <ConfirmDeleteModal
          title="Slet tenant?"
          itemName={deleteConfirm.name}
          warning={`Kunden "${deleteConfirm.name}" fjernes med det samme for alle. Data gemmes i 7 dage, så NordComply kan gendanne den, hvis det var en fejl. Derefter slettes alt endeligt: aflæsninger, historik, noter og adgang. Du får en mail med datoen.`}
          onConfirm={handleDeleteTenant}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </>
  );
}
